import React, { useEffect, useMemo, useState } from 'react';
import { Brain, Check, ClipboardList, ExternalLink, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

const EMPTY_FORM = {
  title: '',
  kind: 'feature',
  area: '',
  purpose: '',
  status: 'proposed',
  priority: 'P2',
  notes: '',
  next_step: '',
};

const KIND_LABELS = {
  feature: 'Feature',
  strategy: 'Strategy',
  idea: 'Idea',
  question: 'Question',
  decision: 'Decision',
};

const CANONICAL_LINKS = [
  { href: 'https://github.com/DarbyEntLLC/dad-partners/blob/main/docs/roadmap.md', label: 'Roadmap' },
  { href: 'https://github.com/DarbyEntLLC/dad-partners/blob/main/docs/strategy-and-operations.md', label: 'Strategy & Operations' },
];

const STATUS_LABELS = {
  proposed: 'Proposed',
  ready: 'Ready',
  in_progress: 'In progress',
  blocked: 'Blocked',
  shipped: 'Shipped',
  parked: 'Parked',
};

const PRIORITY_CLASSES = {
  P0: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  P1: 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300',
  P2: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300',
  P3: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-300',
};

const STATUS_CLASSES = {
  blocked: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  shipped: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  in_progress: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-aqua/30 dark:bg-aqua/10 dark:text-aqua',
};

const formInputClass = 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 dark:border-[#343441] dark:bg-[#16161a] dark:text-white dark:placeholder:text-slate-500';
const selectClass = 'h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 dark:border-[#343441] dark:bg-[#16161a] dark:text-white';

function blankForm() {
  return { ...EMPTY_FORM };
}

export default function FeaturePlanningTab({
  items = [],
  loading = false,
  saving = false,
  editingItem = null,
  onSave,
  onStartEdit,
  onCancelEdit,
  onAnalyze,
  analysisLoadingId = null,
}) {
  const [form, setForm] = useState(blankForm);
  const [showForm, setShowForm] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');

  useEffect(() => {
    if (editingItem) {
      setForm({ ...EMPTY_FORM, ...editingItem });
      setShowForm(true);
    } else {
      setForm(blankForm());
    }
  }, [editingItem]);

  const orderedItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const visibleItems = useMemo(
    () => (kindFilter === 'all' ? orderedItems : orderedItems.filter((item) => (item.kind || 'feature') === kindFilter)),
    [kindFilter, orderedItems],
  );

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.area.trim() || !form.purpose.trim()) {
      toast.error('Title, area, and purpose are required.');
      return;
    }
    const saved = await onSave?.({ ...form, feature_id: editingItem?.feature_id });
    if (saved) {
      setForm(blankForm());
      setShowForm(false);
      onCancelEdit?.();
    }
  };

  const handleCancel = () => {
    setForm(blankForm());
    setShowForm(false);
    onCancelEdit?.();
  };

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-slate-50 to-[#edf6f4] p-6 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#1c2127] dark:via-[#171b20] dark:to-[#11161a]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-cyan-800 dark:text-aqua">
              <ClipboardList className="h-5 w-5" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em]">Features &amp; Operations</p>
            </div>
            <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">Keep the next work visible.</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              Capture the feature and operations work that deserves a clear next move, then keep status and priority honest as the work changes.
            </p>
          </div>
          <Button type="button" onClick={() => { setForm(blankForm()); setShowForm(true); onCancelEdit?.(); }} className="shrink-0 gap-2 bg-slate-900 text-white hover:bg-slate-700 dark:bg-aqua dark:text-slate-950 dark:hover:bg-cyan-200">
            <Plus className="h-4 w-4" /> Add planning item
          </Button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Canonical sources</span>
          {CANONICAL_LINKS.map((link) => (
            <a className="inline-flex items-center gap-1 rounded-full border border-cyan-200 bg-white px-3 py-1.5 font-medium text-cyan-800 hover:bg-cyan-50 dark:border-aqua/20 dark:bg-white/[0.04] dark:text-aqua" href={link.href} key={link.href} rel="noopener noreferrer" target="_blank">
              {link.label} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
        <p className="mt-5 rounded-xl border border-cyan-200 bg-cyan-50/70 px-4 py-3 text-xs leading-5 text-cyan-900 dark:border-aqua/20 dark:bg-aqua/10 dark:text-cyan-100">
          This lane is a working plan. Markdown documents remain authoritative; this queue does not automatically edit the roadmap, change Current Focus, deploy code, or send anything.
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">AI readouts are optional. You are responsible for not entering secrets or member data in planning items.</p>
      </Card>

      {showForm && (
        <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{editingItem ? 'Edit planning item' : 'New planning item'}</h3>
            <Button type="button" variant="ghost" onClick={handleCancel}>Cancel</Button>
          </div>
          <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Title<Input className={formInputClass} value={form.title} onChange={(event) => updateField('title', event.target.value)} maxLength={160} /></label>
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Area<Input className={formInputClass} value={form.area} onChange={(event) => updateField('area', event.target.value)} maxLength={100} placeholder="e.g. Operations, Membership, Marketing" /></label>
            </div>
            <label className="block space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Purpose<Textarea className={formInputClass} value={form.purpose} onChange={(event) => updateField('purpose', event.target.value)} maxLength={1000} rows={3} /></label>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Planning type<select className={selectClass} value={form.kind} onChange={(event) => updateField('kind', event.target.value)}>{Object.entries(KIND_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Status<select className={selectClass} value={form.status} onChange={(event) => updateField('status', event.target.value)}>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
            </div>
            <label className="block max-w-xs space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Priority<select className={selectClass} value={form.priority} onChange={(event) => updateField('priority', event.target.value)}>{['P0', 'P1', 'P2', 'P3'].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Notes<Textarea className={formInputClass} value={form.notes} onChange={(event) => updateField('notes', event.target.value)} maxLength={2000} rows={3} /></label>
              <label className="space-y-1.5 text-sm font-medium text-slate-700 dark:text-slate-200">Next step<Textarea className={formInputClass} value={form.next_step} onChange={(event) => updateField('next_step', event.target.value)} maxLength={1000} rows={3} /></label>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="gap-2 bg-slate-900 text-white hover:bg-slate-700 dark:bg-aqua dark:text-slate-950 dark:hover:bg-cyan-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {saving ? 'Saving...' : editingItem ? 'Save changes' : 'Add item'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="border-slate-200 bg-white p-6 dark:border-[#343441] dark:bg-[#1e1e24]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Planning queue</h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Your items, ordered by priority. Use a type to separate build work from strategy, ideas, questions, and decisions.</p>
          </div>
          <div className="flex items-center gap-2">
            <select aria-label="Filter planning items by type" className={`${selectClass} h-9 w-auto min-w-[130px]`} value={kindFilter} onChange={(event) => setKindFilter(event.target.value)}>
              <option value="all">All types</option>
              {Object.entries(KIND_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <Badge className="border-slate-200 bg-slate-100 text-slate-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-200">{visibleItems.length} items</Badge>
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-aqua" /></div>
        ) : visibleItems.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">{kindFilter === 'all' ? 'No planning items yet. Add one when it has a clear purpose and next step.' : `No ${KIND_LABELS[kindFilter].toLowerCase()} items yet. Add one when it has a clear purpose and next step.`}</p>
        ) : (
          <div className="mt-5 space-y-3">
            {visibleItems.map((item) => (
              <div key={item.feature_id} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-[#343441] dark:bg-[#16161a]">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-slate-900 dark:text-white">{item.title}</h4>
                      <Badge className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-200">{KIND_LABELS[item.kind] || KIND_LABELS.feature}</Badge>
                      <Badge className={PRIORITY_CLASSES[item.priority] || PRIORITY_CLASSES.P2}>{item.priority || 'P2'}</Badge>
                      <Badge className={STATUS_CLASSES[item.status] || 'border-slate-200 bg-white text-slate-700 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300'}>{STATUS_LABELS[item.status] || item.status}</Badge>
                    </div>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{item.area}</p>
                    <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-200">{item.purpose}</p>
                    {item.next_step ? <p className="mt-2 text-xs text-slate-600 dark:text-slate-300"><span className="font-semibold">Next:</span> {item.next_step}</p> : null}
                    {item.notes ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.notes}</p> : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <Button type="button" variant="outline" className="gap-2" onClick={() => onStartEdit?.(item)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    <Button type="button" variant="outline" className="gap-2" disabled={analysisLoadingId === item.feature_id} onClick={() => {
                      const disclosure = 'This sends this planning item to the configured Anthropic provider and may use AI credits. It does not edit the roadmap, Current Focus, or other records. Continue?';
                      if (typeof window === 'undefined' || window.confirm(disclosure)) onAnalyze?.(item);
                    }}>
                      {analysisLoadingId === item.feature_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Brain className="h-3.5 w-3.5" />}
                      {analysisLoadingId === item.feature_id ? 'Analyzing...' : 'Analyze with AI'}
                    </Button>
                  </div>
                </div>
                {item.ai_analysis ? <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50/70 px-4 py-3 text-sm leading-6 text-violet-950 dark:border-violet-400/20 dark:bg-violet-400/10 dark:text-violet-100"><p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-violet-700 dark:text-violet-200"><Brain className="h-3.5 w-3.5" /> AI readout{item.ai_analyzed_at ? ` · ${new Date(item.ai_analyzed_at).toLocaleString(undefined, { timeZone: 'UTC' })} UTC` : ''}</p><p className="whitespace-pre-wrap">{item.ai_analysis}</p></div> : null}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
